import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarClock, TicketCheck } from 'lucide-react';
import { GRACE_CODE_MAX_DAYS, GRACE_EXTENSION_MAX_DAYS, type OwnerGraceCodeDto, type OwnerTenantDeploymentDto } from '@his/shared';
import { api, post } from '@/lib/api';
import { dateTime } from '@/lib/utils';
import { Button, Card, CardContent, CardHeader, CardTitle, DatePicker, Field, Input } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';

function isoFromLocalDate(value: string): string | null {
  if (!value) return null;
  const date = new Date(`${value}T23:59:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function ExtensionDialog({ tenantId, open, onClose, onDone }: { tenantId: string; open: boolean; onClose: () => void; onDone: () => void }) {
  const [until, setUntil] = useState('');
  const [clear, setClear] = useState(false);
  const iso = clear ? null : isoFromLocalDate(until);
  return open ? (
    <OwnerConfirmDialog<OwnerTenantDeploymentDto>
      open={open}
      onClose={onClose}
      title="Extend offline grace"
      description={`The facility's server stays writable without a check-in until this date (at most ${GRACE_EXTENSION_MAX_DAYS} days ahead). It reaches the server at its next check-in — extend before a planned outage.`}
      extraValid={clear || iso !== null}
      confirmLabel={clear ? 'Remove extension' : 'Extend grace'}
      action={({ totpCode, reason }) =>
        api<OwnerTenantDeploymentDto>(`/owner/tenants/${tenantId}/grace-extension`, { method: 'PUT', body: { until: iso, totpCode, reason } })
      }
      onDone={() => {
        setUntil('');
        setClear(false);
        onDone();
      }}
      successMessage="Grace extension saved"
    >
      <Field label="Writable until">
        <DatePicker value={until} disabled={clear} onChange={(value) => setUntil(value)} />
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={clear} onChange={(event) => setClear(event.target.checked)} /> Remove the current extension
      </label>
    </OwnerConfirmDialog>
  ) : null;
}

function GraceCodeDialog({ tenantId, open, onClose, onIssued }: { tenantId: string; open: boolean; onClose: () => void; onIssued: (code: OwnerGraceCodeDto) => void }) {
  const [days, setDays] = useState('3');
  const value = Number.parseInt(days, 10);
  const valid = Number.isInteger(value) && value >= 1 && value <= GRACE_CODE_MAX_DAYS;
  return open ? (
    <OwnerConfirmDialog<OwnerGraceCodeDto>
      open={open}
      onClose={onClose}
      title="Issue an offline grace code"
      description="For an unplanned internet outage: a signed, single-use code the facility's Super Admin enters in Settings → About. Read it out or send it as a file. A server accepts one code every 30 days."
      extraValid={valid}
      confirmLabel="Issue code"
      action={({ totpCode, reason }) => post<OwnerGraceCodeDto>(`/owner/tenants/${tenantId}/grace-code`, { days: value, totpCode, reason })}
      onDone={onIssued}
    >
      <Field label={`Days (1–${GRACE_CODE_MAX_DAYS})`}>
        <Input inputMode="numeric" value={days} onChange={(event) => setDays(event.target.value.replace(/\D/g, '').slice(0, 1))} />
      </Field>
    </OwnerConfirmDialog>
  ) : null;
}

export function TenantGraceCard({ tenantId, deployment }: { tenantId: string; deployment: OwnerTenantDeploymentDto }) {
  const queryClient = useQueryClient();
  const canGrant = useOwnerCapability('deployment.grace');
  const [extensionOpen, setExtensionOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [issued, setIssued] = useState<OwnerGraceCodeDto | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant-deployment', tenantId] });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <CalendarClock className="h-4 w-4" /> Offline grace
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p>
          {deployment.graceExtensionUntil ? `Extended until ${dateTime(deployment.graceExtensionUntil)}` : `No extension — ${deployment.graceDays} days without a check-in`}
        </p>
        {issued && (
          <div className="rounded-md border border-success/30 bg-success/5 p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Grace code — valid until {dateTime(issued.until)}</p>
            <p className="mt-1 select-all break-all font-mono text-xs">{issued.code}</p>
          </div>
        )}
        {canGrant && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" disabled={deployment.deploymentMode !== 'LOCAL'} onClick={() => setExtensionOpen(true)}>
              <CalendarClock className="h-3.5 w-3.5" /> Extend grace
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={!deployment.hardwareFingerprint} onClick={() => setCodeOpen(true)}>
              <TicketCheck className="h-3.5 w-3.5" /> Issue grace code
            </Button>
          </div>
        )}
      </CardContent>
      {extensionOpen && (
        <ExtensionDialog
          tenantId={tenantId}
          open={extensionOpen}
          onClose={() => setExtensionOpen(false)}
          onDone={() => {
            setExtensionOpen(false);
            refresh();
          }}
        />
      )}
      {codeOpen && (
        <GraceCodeDialog
          tenantId={tenantId}
          open={codeOpen}
          onClose={() => setCodeOpen(false)}
          onIssued={(code) => {
            setIssued(code);
            setCodeOpen(false);
          }}
        />
      )}
    </Card>
  );
}
